import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import useSeo from "@/hooks/useSeo";
import {
  ClipboardCheck, ChevronRight, Sparkles, ShieldCheck, Search, FileText, MessageCircle,
  Download, Quote,
} from "lucide-react";

const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

const publicImageUrl = (path) => (path ? `${process.env.REACT_APP_BACKEND_URL}/api/public/testes/image/${path}` : null);

// Split conclusion text into paragraphs and linkify URLs.
// When splitting by a regex with a capture group, matches appear at odd indexes.
const URL_RE = /(https?:\/\/[^\s<>()]+)/gi;
function ConclusionParagraph({ line }) {
  const parts = line.split(URL_RE);
  return (
    <p className="text-sm md:text-base text-foreground/90 leading-relaxed whitespace-pre-line">
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gold underline underline-offset-2 hover:text-primary break-all"
          >
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </p>
  );
}

const WHATSAPP_NUMBER = (process.env.REACT_APP_LEAD_WHATSAPP || "").replace(/\D+/g, "");

// ─── Question widgets (mirror /viva-melhor visuals exactly) ─────────────
function ScaleInput({ value, onChange, id }) {
  return (
    <div className="flex gap-1 flex-wrap" data-testid={`scale-${id}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`w-10 h-10 rounded-sm border text-sm font-semibold transition-colors ${
            value === n ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
          }`}
          data-testid={`scale-${id}-${n}`}
        >
          {n}
        </button>
      ))}
      <span className="ml-2 self-center text-[11px] text-muted-foreground">1 = muito baixo · 5 = excelente</span>
    </div>
  );
}

function YesNoInput({ value, onChange, id }) {
  return (
    <div className="flex gap-2" data-testid={`yesno-${id}`}>
      {[["sim", "Sim"], ["nao", "Não"]].map(([v, label]) => {
        const selected = (value === true && v === "sim") || (value === false && v === "nao");
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v === "sim")}
            className={`px-4 py-2 rounded-sm border text-sm font-semibold transition-colors ${
              selected ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
            }`}
            data-testid={`yesno-${id}-${v}`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function QuestionField({ q, value, onChange }) {
  if (q.type === "scale5") return <ScaleInput value={value} onChange={onChange} id={q.id} />;
  if (q.type === "yes_no") return <YesNoInput value={value} onChange={onChange} id={q.id} />;
  return null;
}

// ─── Screen 1 — Lead capture (identical to /viva-melhor) ─────────────────
function IntroScreen({ testDef, onStart }) {
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [loading, setLoading] = useState(false);

  const start = async () => {
    if (!name.trim()) { toast.error("Digite seu nome."); return; }
    const digits = (whatsapp || "").replace(/\D+/g, "");
    if (digits.length < 10) { toast.error("Digite um WhatsApp válido (DDD + número)."); return; }
    setLoading(true);
    try {
      const { data } = await publicApi.post(`/public/testes/${testDef.slug}/start`, {
        name: name.trim(), whatsapp: digits,
      });
      onStart(data.token, name.trim());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível iniciar o teste. Tente novamente.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10" data-testid="test-intro">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-sm bg-primary text-primary-foreground grid place-items-center mx-auto mb-4">
            <ClipboardCheck className="w-7 h-7" />
          </div>
          <div className="overline text-gold mb-2 tracking-[0.24em]">Centro de Performance Farmacêutica</div>
          <h1 className="brand-wordmark uppercase text-3xl md:text-4xl font-bold text-primary leading-tight">
            {testDef.name}
          </h1>
          {testDef.description && (
            <p className="text-sm text-muted-foreground mt-4 max-w-sm mx-auto leading-relaxed">
              {testDef.description}
            </p>
          )}
        </div>

        <div className="bg-card border border-border rounded-sm p-6 space-y-4">
          <div>
            <label className="overline text-muted-foreground mb-1 block">Nome</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome"
              className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-sm"
              data-testid="lead-name-input"
              autoComplete="name"
            />
          </div>
          <div>
            <label className="overline text-muted-foreground mb-1 block">WhatsApp</label>
            <input
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="(11) 99999-8888"
              className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-sm"
              data-testid="lead-whatsapp-input"
              inputMode="tel"
              autoComplete="tel"
            />
          </div>
          <button
            onClick={start}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
            data-testid="lead-start-btn"
          >
            {loading ? "Preparando…" : (<><Sparkles className="w-4 h-4" /> Iniciar Teste</>)}
          </button>
          <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
            Ao continuar, você concorda em compartilhar suas respostas com nosso farmacêutico
            para receber um feedback personalizado.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Screen 2 — Questionnaire (single card, all questions) ───────────────
function QuestionnaireScreen({ token, patientName, onDone }) {
  const [state, setState] = useState({ loading: true, error: null, session: null });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/testes/session/${token}`);
        setState({ loading: false, error: null, session: data });
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Não foi possível carregar o teste.", session: null });
      }
    })();
  }, [token]);

  if (state.loading) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground overline">Carregando…</div>;
  }
  if (state.error || !state.session) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="bg-card border border-destructive/30 rounded-sm p-6 max-w-md text-center">
          <h2 className="brand-wordmark uppercase text-lg font-bold mb-2">Ops</h2>
          <p className="text-sm text-muted-foreground">{state.error}</p>
        </div>
      </div>
    );
  }

  const testDef = state.session.test;
  const setAnswer = (qid, v) => setAnswers((prev) => ({ ...prev, [qid]: v }));

  const answered = testDef.questions.filter((q) => answers[q.id] !== undefined && answers[q.id] !== null).length;
  const pct = testDef.questions.length ? Math.round((answered / testDef.questions.length) * 100) : 0;

  const submit = async () => {
    if (answered < testDef.questions.length) {
      toast.error("Responda todas as perguntas antes de enviar.");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.post(`/public/testes/session/${token}/submit`, { answers });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar respostas."); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="test-questionnaire">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="overline text-gold mb-2 inline-flex items-center gap-2">
            <ClipboardCheck className="w-3.5 h-3.5" /> {testDef.name}
          </div>
          <h1 className="brand-wordmark text-2xl md:text-3xl font-bold tracking-tight uppercase text-primary">
            {patientName ? `Olá, ${patientName}!` : "Vamos lá!"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Responda com sinceridade — leva ~3 minutos.
          </p>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-1 text-xs text-muted-foreground">
            <span>{answered} de {testDef.questions.length} respondidas</span>
            <span>{pct}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-sm overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-5 md:p-6 space-y-5">
          {testDef.questions.map((q, idx) => (
            <div key={q.id} data-testid={`q-${q.id}`}>
              <label className="block text-sm font-medium mb-2">
                <span className="text-muted-foreground mr-1.5">{idx + 1}.</span>
                {q.text}
              </label>
              {q.image_path && (
                <div className="mb-3">
                  <img
                    src={publicImageUrl(q.image_path)}
                    alt={`Imagem de referência — ${q.text}`}
                    className="w-full max-w-md h-auto rounded-md border border-border shadow-sm mx-auto block"
                    loading="lazy"
                    data-testid={`q-image-${q.id}`}
                  />
                </div>
              )}
              <QuestionField q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={submit} disabled={submitting}
            className="px-6 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 inline-flex items-center gap-1"
            data-testid="test-submit-btn"
          >
            {submitting ? "Enviando…" : (<>Enviar respostas <ChevronRight className="w-4 h-4" /></>)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Screen 3 — Smart result report (reuses /viva-melhor look) ───────────
function ScoreBadge({ value, color, size = "md" }) {
  const palette = {
    green: "bg-emerald-100 text-emerald-800 border-emerald-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
    red:   "bg-rose-100 text-rose-800 border-rose-300",
    muted: "bg-muted text-muted-foreground border-border",
  };
  const cls = palette[color] || palette.muted;
  const sizeCls = size === "lg"
    ? "px-4 py-2 text-2xl md:text-3xl font-bold rounded-md"
    : "px-2.5 py-1 text-sm font-semibold rounded-sm";
  return (
    <span className={`inline-flex items-baseline gap-1 border ${cls} ${sizeCls}`}>
      {value === null || value === undefined ? "—" : value}
    </span>
  );
}

export function DynamicTestReport({ token, patientName }) {
  const [state, setState] = useState({ loading: true, error: null, report: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/testes/result/${token}`);
        setState({ loading: false, error: null, report: data });
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Não foi possível carregar seu relatório.", report: null });
      }
    })();
  }, [token]);

  if (state.loading) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground overline">Preparando seu relatório…</div>;
  }
  if (state.error || !state.report) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="bg-card border border-destructive/30 rounded-sm p-6 max-w-md text-center">
          <h2 className="brand-wordmark uppercase text-lg font-bold mb-2">Ops</h2>
          <p className="text-sm text-muted-foreground">{state.error}</p>
        </div>
      </div>
    );
  }

  const r = state.report;
  const band = r.score?.band;
  const bandColor = band?.color || "muted";
  const total = r.score?.total ?? null;
  const ctaMsg = `Olá, acabei de fazer o teste "${r.test_name}" (${r.submission_id}) e quero agendar meu acompanhamento`;
  const whatsappHref = WHATSAPP_NUMBER
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(ctaMsg)}`
    : null;

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="test-report">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-sm bg-primary text-primary-foreground grid place-items-center mx-auto mb-3">
            <FileText className="w-6 h-6" />
          </div>
          <div className="overline text-gold mb-1 tracking-[0.24em] text-[10px]">Centro de Performance Farmacêutica</div>
          <h1 className="brand-wordmark uppercase text-2xl md:text-3xl font-bold text-primary leading-tight">
            Protocolo de Análise <span className="text-gold">#{r.submission_id}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {r.test_name}
            {(patientName || r.patient_name) && <> · Análise de <b className="text-foreground">{patientName || r.patient_name}</b></>}
          </p>
        </div>

        <div className="bg-card border border-border rounded-md p-6 mb-6 text-center" data-testid="report-global-score">
          <div className="overline text-muted-foreground mb-3 text-[11px]">Pontuação total</div>
          <ScoreBadge value={total} color={bandColor} size="lg" />
          {band?.label && (
            <p className="text-sm text-foreground font-semibold mt-3">
              Classificação: <span className={
                bandColor === "green" ? "text-emerald-700" :
                bandColor === "amber" ? "text-amber-700" :
                bandColor === "red"   ? "text-rose-700"   : "text-muted-foreground"
              }>{band.label}</span>
            </p>
          )}
        </div>

        {/* Per-question breakdown */}
        <div className="space-y-2 mb-6">
          {(r.questions || []).map((q, idx) => {
            const contrib = r.score?.details?.[q.id] ?? 0;
            const answer = r.answers?.[q.id];
            let answerText = "—";
            if (q.type === "yes_no") answerText = answer === true ? "Sim" : answer === false ? "Não" : "—";
            else if (q.type === "scale5") answerText = answer ? `${answer}/5` : "—";
            return (
              <div key={q.id} className="bg-card border border-border rounded-sm p-3 md:p-4 flex items-start justify-between gap-3" data-testid={`report-q-${q.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-foreground leading-relaxed">
                    <span className="text-muted-foreground mr-1.5">{idx + 1}.</span>{q.text}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Resposta: <b className="text-foreground">{answerText}</b> · Peso: {q.weight}
                  </div>
                </div>
                <span className="text-xs font-semibold text-primary px-2 py-1 rounded-sm bg-primary/5 border border-primary/20 shrink-0">
                  +{contrib}
                </span>
              </div>
            );
          })}
        </div>

        {/* Considerações do Especialista — Parecer final dinâmico */}
        {r.conclusion_text && r.conclusion_text.trim() && (
          <div
            className="mb-6 rounded-md border border-gold/30 bg-gradient-to-br from-primary/[0.03] to-transparent p-5 md:p-6 relative overflow-hidden"
            data-testid="report-conclusion"
          >
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gold" aria-hidden />
            <div className="flex items-center gap-2 mb-3 pl-1">
              <Quote className="w-4 h-4 text-gold shrink-0" />
              <h3 className="brand-wordmark text-sm md:text-base font-bold uppercase text-primary tracking-wider">
                Considerações do Especialista
              </h3>
            </div>
            <div className="space-y-3 pl-1">
              {r.conclusion_text.split(/\n\n+/).map((para, i) => (
                <ConclusionParagraph key={i} line={para} />
              ))}
            </div>
          </div>
        )}

        <div className="bg-primary/5 border border-primary/20 rounded-md p-5 md:p-6 text-center" data-testid="report-cta">
          <ShieldCheck className="w-8 h-8 text-primary mx-auto mb-3" />
          <p className="text-sm md:text-base text-foreground leading-relaxed max-w-md mx-auto">
            Estas hipóteses precisam de <b>confirmação clínica</b>. Agende sua consulta para
            montar seu <b>protocolo de precisão</b>.
          </p>
          <a
            href={whatsappHref || "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { if (!whatsappHref) e.preventDefault(); }}
            className={`mt-5 inline-flex items-center justify-center gap-2 rounded-md px-6 py-3.5 text-sm md:text-base font-semibold transition-colors w-full sm:w-auto min-h-[48px] ${
              whatsappHref
                ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm active:scale-[0.99]"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            }`}
            data-testid="report-whatsapp-cta"
          >
            <MessageCircle className="w-5 h-5" /> Agendar minha consulta no WhatsApp
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/public/testes/result/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-primary underline"
            data-testid="report-download-pdf"
          >
            <Download className="w-3.5 h-3.5" /> Baixar laudo em PDF
          </a>
        </div>

        <p className="text-[11px] text-muted-foreground text-center mt-6 italic max-w-md mx-auto leading-relaxed">
          Este relatório é orientativo e não substitui avaliação clínica presencial.
        </p>
      </div>
    </div>
  );
}

// ─── Root page ───────────────────────────────────────────────────────────
export default function PublicDynamicTestPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, testDef: null });
  const [step, setStep] = useState("intro");
  const [token, setToken] = useState(null);
  const [patientName, setPatientName] = useState("");
  useSeo({
    title: state.testDef ? `${state.testDef.name} | Centro de Performance Farmacêutica` : null,
    description: state.testDef?.description,
    path: `/testes/${slug}`,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/testes/${slug}`);
        setState({ loading: false, error: null, testDef: data });
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Teste indisponível.", testDef: null });
      }
    })();
  }, [slug]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="p-16 text-center text-muted-foreground overline">Carregando…</div>
      </div>
    );
  }
  if (state.error || !state.testDef) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="p-16 text-center">
          <p className="text-sm text-destructive mb-4">{state.error}</p>
          <button onClick={() => navigate("/testes")} className="text-sm underline text-primary">Voltar aos testes</button>
        </div>
      </div>
    );
  }

  if (step === "done" && token) return <DynamicTestReport token={token} patientName={patientName} />;
  if (step === "questionnaire" && token) {
    return (
      <QuestionnaireScreen
        token={token}
        patientName={patientName}
        onDone={() => setStep("done")}
      />
    );
  }
  return (
    <IntroScreen
      testDef={state.testDef}
      onStart={(newToken, name) => {
        setToken(newToken);
        setPatientName(name);
        setStep("questionnaire");
      }}
    />
  );
}
